import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const FailedDeliveryAttempt: React.FC = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="When a delivery attempt fails, it’s essential to act quickly to address the situation and ensure your shipment reaches the retailer successfully. Here’s what you can do:"
          description={{
            text: "A step-by-step guide to failed delivery attempts",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Contact the carrier:</BoldFormatting> Reach out to the carrier immediately to understand the reason for the failed delivery attempt and discuss the possibility of re-attempting the delivery."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "The first step title and content in the failed delivery attempt guide",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Message your retailer:</BoldFormatting> Inform the retailer about the failed delivery attempt and any updates or changes that might affect the delivery timeline."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "The second step title in the failed delivery attempt guide",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Verify the shipping address:</BoldFormatting> Double-check the shipping address provided by the retailer. If there are any discrepancies, confirm the correct address with the retailer."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "The third step title in the failed delivery attempt guide",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Arrange for redelivery:</BoldFormatting> Once the address is confirmed and any issues are resolved, coordinate with the carrier to schedule a redelivery at the earliest convenience."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "The fourth step title in the failed delivery attempt guide",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
    </Column>
  );
};

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
